import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faChevronDown,
  faChevronUp,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [isPhoneListOpen, setIsPhoneListOpen] = useState(false);

  const togglePhoneList = () => setIsPhoneListOpen(!isPhoneListOpen);

  return (
    <footer className="bg-primary text-white pt-24 pb-6 mt-24">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* About Section */}
          <div>
            <h3 className="text-xl text-white font-semibold mb-6 relative after:content-[''] after:absolute after:left-0 after:bottom-[-10px] after:w-12 after:h-0.5 after:bg-accent">
              Servizio Bolletta
            </h3>
            <p className="text-white/80 mb-4 text-sm leading-relaxed">
              Servizio indipendente di consulenza gratuita e comparazione offerte per luce, gas e internet.
              Non siamo il servizio clienti ufficiale di alcun fornitore di energia.
            </p>
            <div className="space-y-2">
              <div className="flex items-center">
                <FontAwesomeIcon icon={faPhone} className="text-accent mr-2 text-sm" />
                <a href="tel:+390240055045" className="text-white/80 text-sm">02 4005 5045</a>
              </div>
              <div className="flex items-center">
                <FontAwesomeIcon icon={faEnvelope} className="text-accent mr-2 text-sm" />
                <a href="mailto:info@serviziobolletta.it" className="text-white/80 text-sm">info@serviziobolletta.it</a>
              </div>
              <div className="flex items-center">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="text-accent mr-2 text-sm" />
                <span className="text-white/80 text-sm">Via Filippo Argelati 10, Milano</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl text-white font-semibold mb-6 relative after:content-[''] after:absolute after:left-0 after:bottom-[-10px] after:w-12 after:h-0.5 after:bg-accent">
              Link Rapidi
            </h3>
            <ul className="space-y-2">
              {[
                { to: '/', label: 'Home' },
                { to: '/chi-siamo', label: 'Chi Siamo' },
                { to: '/offerte', label: 'Offerte' },
                { to: '/contattaci', label: 'Contattaci' },
                { to: '/faq', label: 'Domande Frequenti' },
              ].map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-white/80 hover:text-accent transition-all duration-300 inline-block hover:translate-x-1 text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Informazioni Legali */}
          <div>
            <h3 className="text-xl text-white font-semibold mb-6 relative after:content-[''] after:absolute after:left-0 after:bottom-[-10px] after:w-12 after:h-0.5 after:bg-accent">
              Informazioni Legali
            </h3>
            <ul className="space-y-2">
              <li>
                <Link to="/privacy-policy" className="text-white/80 hover:text-accent transition-all duration-300 inline-block hover:translate-x-1 text-sm">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/cookie-policy" className="text-white/80 hover:text-accent transition-all duration-300 inline-block hover:translate-x-1 text-sm">
                  Cookie Policy
                </Link>
              </li>
              <li>
                <Link to="/termini-e-condizioni" className="text-white/80 hover:text-accent transition-all duration-300 inline-block hover:translate-x-1 text-sm">
                  Termini e Condizioni
                </Link>
              </li>
              <li>
                <Link to="/elenco-fornitori-societa-terze" className="text-white/80 hover:text-accent transition-all duration-300 inline-block hover:translate-x-1 text-sm">
                  Elenco Fornitori
                </Link>
              </li>
            </ul>
          </div>

          {/* Servizio Clienti */}
          <div>
            <h3 className="text-xl text-white font-semibold mb-6 relative after:content-[''] after:absolute after:left-0 after:bottom-[-10px] after:w-12 after:h-0.5 after:bg-accent">
              Contatti
            </h3>
            <button
              onClick={togglePhoneList}
              className="w-full flex items-center justify-between bg-primary-light/20 hover:bg-primary-light/30 px-4 py-2 rounded-md transition-colors duration-200 mb-2"
            >
              <span className="text-white font-medium text-sm">Numeri di telefono</span>
              <FontAwesomeIcon icon={isPhoneListOpen ? faChevronUp : faChevronDown} className="text-accent transition-transform duration-300" />
            </button>
            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isPhoneListOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 pt-2 pb-1 px-2">
                {['02 4005 5045', '02 9475 3137', '02 4005 5113', '02 9475 3134', '02 4074 1001', '02 4873 1500', '02 9475 3133', '02 4005 5268', '02 4005 5051', '02 4074 1451'].map((num) => (
                  <a key={num} href={`tel:+39${num.replace(/\s/g, '')}`} className="text-white/80 hover:text-accent text-sm flex items-center">
                    <FontAwesomeIcon icon={faPhone} className="mr-1 text-accent" size="xs" />
                    {num}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mb-8 p-5 rounded-lg bg-white/5 border border-white/10">
          <div className="flex items-start gap-3">
            <FontAwesomeIcon icon={faInfoCircle} className="text-accent mt-0.5 flex-shrink-0" />
            <p className="text-white/60 text-xs leading-relaxed">
              <strong className="text-white/80">Disclaimer:</strong> Servizio Bolletta è un servizio indipendente di comparazione 
              e assistenza commerciale gestito da GT Energie Srl (P.IVA: IT02812720809, sede legale: Via Filippo Argelati 10, Milano). 
              Non siamo il servizio clienti ufficiale di alcun fornitore di energia, gas o servizi internet. 
              Il servizio è gratuito per l'utente e non implica alcun obbligo di sottoscrizione. 
              L'eventuale stipula di un contratto con un fornitore è una decisione libera e autonoma del cliente. 
              In alcuni casi Servizio Bolletta potrebbe percepire una commissione commerciale dai fornitori partner in caso di attivazione.
              Le informazioni e le offerte presenti su questo sito hanno carattere puramente informativo e possono variare.
            </p>
          </div>
        </div>

        <div className="mt-8 pt-4 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-white/50 text-xs text-center md:text-left">
            <p>&copy; {currentYear} GT Energie Srl — P.IVA IT02812720809</p>
            <p>Sede legale: Via Filippo Argelati 10, 20143 Milano (MI)</p>
          </div>
          <div className="flex flex-wrap gap-6 justify-center">
            <Link to="/privacy-policy" className="text-white/50 hover:text-accent text-xs transition-colors">Privacy</Link>
            <Link to="/cookie-policy" className="text-white/50 hover:text-accent text-xs transition-colors">Cookie</Link>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("sb-open-consent"))}
              className="text-white/50 hover:text-accent text-xs transition-colors"
            >
              Gestisci cookie
            </button>
            <Link to="/termini-e-condizioni" className="text-white/50 hover:text-accent text-xs transition-colors">T&C</Link>
            <Link to="/chi-siamo" className="text-white/50 hover:text-accent text-xs transition-colors">Chi Siamo</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
